import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { ArrowRight, Gift } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import { SetuMark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthPage({ mode }) {
  const isSignup = mode === "signup";
  const { t } = useLang();
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "", name: "", company: "" });
  const [consent, setConsent] = useState(false);
  const [refCode, setRefCode] = useState(params.get("ref") || "");
  const [refererName, setRefererName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!refCode || refCode.length < 3) { setRefererName(""); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/referral/validate/${encodeURIComponent(refCode)}`)
        .then((r) => setRefererName(r.data.valid ? r.data.referrer_name : ""))
        .catch(() => setRefererName(""));
    }, 350);
    return () => clearTimeout(t);
  }, [refCode]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (isSignup && !consent) { toast.error("Please accept the Terms & Privacy Policy"); return; }
    setBusy(true);
    try {
      if (isSignup) {
        await register({ ...form, consent: true, referral_code: refCode || undefined });
        toast.success("Account created. Welcome!");
      } else {
        await login(form.email, form.password);
        toast.success("Signed in.");
      }
      nav("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleSignIn = () => {
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="auth-logo">
            <div className="w-8 h-8 rounded-md bg-primary grid place-items-center text-primary-foreground"><SetuMark size={22} strokeWidth={2.6} /></div>
            <span className="font-heading text-lg tracking-tight">Setubuild.OS</span>
          </Link>
          <div className="flex items-center gap-2"><LanguageSwitcher /><ThemeToggle /></div>
        </div>
      </header>

      <main className="flex-1 grid lg:grid-cols-2">
        <div className="hidden lg:block relative border-r border-border blueprint-grid">
          <div className="absolute inset-0 grid place-items-center p-12">
            <div>
              <h2 className="font-display text-5xl md:text-6xl leading-tight max-w-md">
                Every worker <span className="font-display-italic text-primary">verified.</span><br />
                Every scan <span className="font-display-italic text-primary">logged.</span>
              </h2>
              <p className="mt-6 text-muted-foreground max-w-md">Your projects, incidents, inventory, and reports are ready the moment you sign in.</p>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 md:p-12 flex items-center">
          <form onSubmit={submit} className="w-full max-w-md space-y-4" data-testid="auth-form">
            <div className="mono text-xs uppercase tracking-widest text-muted-foreground">
              {isSignup ? "Create account" : "Welcome back"}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl">{isSignup ? t("sign_up") : t("sign_in")}</h1>

            {isSignup && (
              <>
                <div>
                  <label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{t("full_name")}</label>
                  <input required name="name" value={form.name} onChange={onChange} data-testid="auth-name"
                    className="w-full mt-1 border border-border bg-card px-3 py-3 text-base focus:outline-none focus:border-primary" />
                </div>
                <div>
                  <label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{t("company")}</label>
                  <input name="company" value={form.company} onChange={onChange} data-testid="auth-company"
                    className="w-full mt-1 border border-border bg-card px-3 py-3 text-base focus:outline-none focus:border-primary" />
                </div>
              </>
            )}
            <div>
              <label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{t("email")}</label>
              <input required type="email" name="email" value={form.email} onChange={onChange} data-testid="auth-email"
                inputMode="email" autoComplete="email"
                className="w-full mt-1 border border-border bg-card px-3 py-3 text-base focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{t("password")}</label>
              <input required type="password" name="password" value={form.password} onChange={onChange} data-testid="auth-password"
                autoComplete={isSignup ? "new-password" : "current-password"}
                className="w-full mt-1 border border-border bg-card px-3 py-3 text-base focus:outline-none focus:border-primary" />
              {!isSignup && (
                <Link to="/forgot-password" data-testid="auth-forgot"
                  className="mono text-[10px] uppercase tracking-widest text-primary hover:underline mt-1 inline-block">
                  Forgot password?
                </Link>
              )}
            </div>

            {isSignup && (
              <div>
                <label className="mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                  <Gift className="h-3 w-3" /> Referral code (optional)
                </label>
                <input value={refCode} onChange={(e) => setRefCode(e.target.value.toUpperCase())}
                  data-testid="auth-refcode" aria-label="Referral code"
                  className="w-full mt-1 border border-border bg-card px-3 py-3 text-base mono focus:outline-none focus:border-primary" />
                {refererName && (
                  <div className="mono text-[10px] uppercase tracking-widest text-primary mt-1">
                    ✓ Invited by {refererName} — you both get 1 month free on subscription
                  </div>
                )}
              </div>
            )}

            <button type="submit" disabled={busy} data-testid="auth-submit"
              className="btn-sharp w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3.5 font-medium disabled:opacity-50 text-base">
              {busy ? "…" : (isSignup ? t("sign_up") : t("sign_in"))} <ArrowRight className="h-4 w-4" />
            </button>

            {isSignup && (
              <label className="flex items-start gap-2 text-xs text-muted-foreground select-none">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  data-testid="auth-consent"
                  className="mt-0.5 accent-primary" />
                <span>
                  I agree to the <Link to="/terms" className="text-primary underline" target="_blank">Terms of Service</Link>
                  {" "}and <Link to="/privacy" className="text-primary underline" target="_blank">Privacy Policy</Link>,
                  and I consent to Setubuild.OS processing my data as described.
                </span>
              </label>
            )}

            <div className="flex items-center gap-3 my-3">
              <div className="flex-1 h-px bg-border" />
              <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{t("or_continue")}</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            <button type="button" onClick={googleSignIn} disabled={busy} data-testid="google-auth-btn"
              className="btn-sharp w-full inline-flex items-center justify-center gap-2 border border-border bg-card py-3 font-medium hover:bg-muted">
              <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              {t("google_login")}
            </button>

            <p className="text-sm text-muted-foreground text-center">
              {isSignup ? "Have an account? " : "New here? "}
              <Link to={isSignup ? "/login" : "/signup"} className="text-primary underline" data-testid="auth-switch">
                {isSignup ? t("sign_in") : t("sign_up")}
              </Link>
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}
